/**
 * Protocol constants — port of mpshared/constants.h (XMSG_* ids) + the message-size
 * and string-field constants the codec needs. Kept 1:1 with the C++ so verification is
 * name-for-name.
 */

// Current protocol version (constants.h:9). Handshake must match this. Bumped 0x17->0x18 in
// lockstep with mpshared/constants.h (kdwProtocolCurrent), which dropped the protocol-17 login
// compat. The login wire format is unchanged (XMSG.LOGIN 0x16); only the handshake version gate
// moves, so a 0x18 client (this build) handshakes with the DO and 0x17 clients no longer do.
export const PROTOCOL_CURRENT = 0x00000018;

// Client ids (mpht.h:14-15). kdwClientLoadTest is accepted for anon synthetic clients.
export const CLIENT_ID = 0x00000001; // kdwClientID
export const CLIENT_LOADTEST = 0x00000002; // kdwClientLoadTest
// The TS web client advertises this id in its handshake so the DO knows the client is reconnect-capable
// (CsResumeGame). A mid-game drop from a CLIENT_TS slot gets the reconnect grace-hold; a WASM/bot drop
// (which can never come back) AI-converts immediately, as before. See tasks/reconnect-resume-plan.md.
export const CLIENT_TS = 0x00000003;

// XMsg frame fixed overhead: [u16 size][u8 id]
export const XMSG_SIZE_FIELD = 2;
export const XMSG_ID_FIELD = 1;
export const XMSG_FIXED = XMSG_SIZE_FIELD + XMSG_ID_FIELD; // 3

// XMSG_* message ids (constants.h:13-67)
export const XMSG = {
  NONE: 0x00,
  HANDSHAKE: 0x01,
  HANDSHAKERESULT: 0x02,
  SHOWMESSAGE: 0x03,
  ECHO: 0x04,
  PROTOCOLERROR: 0x05,
  LOGIN_PROTOCOL17: 0x12,
  LOGIN: 0x16,
  LOGINRESULT: 0x13,
  SIGNOUT: 0x14,
  SIGNOUTRESULT: 0x15,
  LOBBYJOIN: 0x20,
  LOBBYJOINRESULT: 0x21,
  LOBBYCREATEROOM: 0x22,
  LOBBYCREATEROOMRESULT: 0x23,
  LOBBYCANJOINROOM: 0x24,
  LOBBYCANJOINROOMRESULT: 0x25,
  LOBBYLURKERCOUNT: 0x26,
  LOBBYADDROOM: 0x27,
  LOBBYREMOVEROOM: 0x28,
  LOBBYUPDATEROOM: 0x29,
  LOBBYLEAVE: 0x2a,
  LOBBYLEAVERESULT: 0x2b,
  LOBBYRECEIVECHAT: 0x2c,
  ROOMJOIN: 0x30,
  ROOMJOINRESULT: 0x31,
  ROOMADDPLAYER: 0x32,
  ROOMREMOVEPLAYER: 0x33,
  ROOMSENDCHAT: 0x34,
  ROOMRECEIVECHAT: 0x35,
  ROOMADDGAME: 0x36,
  ROOMREMOVEGAME: 0x37,
  ROOMGAMEINPROGRESS: 0x38,
  ROOMGAMEPLAYERNAMES: 0x39,
  ROOMSTATUSCOMPLETE: 0x3a,
  ROOMCREATEGAME: 0x3b,
  ROOMCREATEGAMERESULT: 0x3c,
  ROOMCANJOINGAME: 0x3d,
  ROOMCANJOINGAMERESULT: 0x3e,
  ROOMLEAVE: 0x3f,
  ROOMLEAVERESULT: 0x40,
  GAMEJOIN: 0x50,
  GAMEJOINRESULT: 0x51,
  GAMESENDCHAT: 0x52,
  GAMERECEIVECHAT: 0x53,
  GAMENETMESSAGE: 0x54,
  GAMEUPDATEEMPTY: 0x55,
  GAMEUPDATERESULT: 0x56,
  GAMEKILLED: 0x57,
  GAMELEAVE: 0x58,
  GAMELEAVERESULT: 0x59,
  GAMEUPDATEALLIES: 0x5a,
  DISCONNECTSHAREDACCOUNTS: 0x60,
} as const;

// NetMessage ids (netmessage.h:34-59) — the inner messages wrapped by GAMENETMESSAGE/etc.
export const NMID = {
  ScCantAcceptMoreConnections: 1,
  ScServerInfo: 2,
  CsClientCommands: 3,
  ScUpdate: 4,
  ScGameParams: 5,
  ScBeginGame: 6,
  CsClientReady: 7,
  CsPlayerJoin: 8,
  ScPlayersUpdate: 9,
  CsUpdateResult: 10,
  CsConnect: 12,
  ScLagNotify: 13,
  ScPlayerDisconnect: 14,
  CsPlayerResign: 15,
  CsRequestBeginGame: 16,
  ScBeginGameFail: 17,
  CsKillLaggingPlayer: 18,
  ScSyncError: 19,
  CsLagAcknowledge: 20,
  CsWinStats: 21,
  ScCheckWin: 22,
  CsChallengeWin: 23,
  // TS-stack-only control message (NOT a C++ netmessage id — those are 1..23, netmessage.h:34-59). The
  // creator's client sends this over its authenticated game socket to add a bot to its OWN staging game,
  // so no shared token is exposed in the client. The high value avoids any collision with a C++ id; the
  // shipped WASM client never sends it, and decodeInner returns `rawinner` for it (no codec change).
  CsAddBotRequest: 0xb0,
  // TS-stack-only reconnect/resume (also NOT C++ netmessage ids). A reconnect-capable client that
  // dropped mid-game re-binds to its held slot and resumes lockstep. CsResumeGame is client→server
  // (the WASM client never sends it); ScResumeResult is only ever sent in reply to a CsResumeGame
  // (so the WASM client never receives it) — both safe by construction, both `rawinner` (no codec
  // change). See tasks/reconnect-resume-plan.md.
  CsResumeGame: 0xb1, // body: [u32 gameId][u32 lastBlock] — the last block the client received (the DO replays everything after it)
  ScResumeResult: 0xb2, // body: [u8 ok] — 1 = re-bound (catch-up ScUpdate frames follow), 0 = fall back
  // TS-stack-only (also NOT a C++ netmessage id). The creator removes a bot it previously added to its OWN
  // staging game — the staging roster's bot rows are selectable, and selecting one flips the ADD button to
  // REMOVE. Creator-scoped by the connection (no shared token), staging-only; the WASM client never sends
  // it, and decodeInner returns `rawinner` for it (the handler reads the pid from the inner bytes directly).
  CsRemoveBotRequest: 0xb3, // body: [u32 pid] — the staging-roster pid of the bot slot to remove
  // TS-stack-only (also NOT a C++ netmessage id). Server→client: the finished game's replay is now in the
  // wi-replays R2 archive at `key`. Broadcast to every still-connected client the moment the game is WON,
  // so the end-of-game summary can light up a "VIEW REPLAY" option (a player eliminated earlier who keeps
  // the summary up gets it too). The WASM client never receives it (decodeInner → rawinner; no codec
  // change); the body is the UTF-8 archive key `<yyyy-mm-dd>/<gameId>.wirep`.
  ScReplayAvailable: 0xb4, // body: [utf8 replayKey]
} as const;

// String field max sizes (mpht.h). The codec framing is length-prefixed (XMsgS* writes
// `[u16 cb][cb bytes]`, cb = strlen+1) — these are the per-FIELD caps the C++ FromBuffer
// enforced (rejecting oversized frames). The DO enforces them at the message handlers
// (endpoint.ts) to reproduce that reject-oversized behavior.
export const KCB = {
  USERNAME_MAX: 64,
  TOKEN_MAX: 256,
  DID_MAX: 34,
  PLATFORM_MAX: 32,
  CHAT_MAX: 1024, // kcbChatMax
  ROOMNAME: 32,
  PASSWORD: 32,
  PLAYERNAME: 32,
  LEVELTITLE: 40,
  FILENAME: 29,
} as const;

// Reverse lookup id -> name (for transcript labeling / debugging).
export const XMSG_NAME: Record<number, string> = Object.fromEntries(
  Object.entries(XMSG).map(([k, v]) => [v, k]),
);
export const NMID_NAME: Record<number, string> = Object.fromEntries(
  Object.entries(NMID).map(([k, v]) => [v, k]),
);
